import { useEffect, useState } from "react";
import { api } from "../api";

/**
 * Bank accounts, on the Settings page (super admin only). These are the
 * "PAYMENT DETAILS" block at the bottom of an invoice; which account a client
 * is billed from is picked per client, on the Users page.
 *
 * The details are an ordered list of label/value rows rather than fixed fields
 * on purpose: a Canadian account prints Institution and Transit numbers, a
 * European one an IBAN and BIC, and the invoice has to show whatever that bank
 * actually needs. Rows print in the order they're listed here.
 */
const BLANK = { name: "", currency: "", note: "", fields: [{ label: "", value: "" }] };

/**
 * A label line: "Bank name:" on its own, or "Bank name: Citibank" inline.
 *
 * The character class is deliberately narrow (no commas, no digits-then-colon
 * runs) so an ordinary value line that happens to contain a colon isn't
 * mistaken for a new field. `https` is the one plausible false positive, hence
 * the "//" check in the parser.
 */
const LABEL_RE = /^([A-Za-z][A-Za-z0-9 ()/&.'-]{0,39}):\s*(.*)$/;

/**
 * Parses a block copied straight out of a bank or Payoneer page into the
 * label/value rows an invoice prints, keeping the order it was pasted in:
 *
 *   Bank name:            ->  { label: "Bank name",  value: "Citibank" }
 *   Citibank
 *
 *   Bank address:         ->  { label: "Bank address",
 *   111 Wall Street            value: "111 Wall Street New York, NY 10043" }
 *   New York, NY 10043
 *
 * A value can run over several lines (an address usually does); a blank line
 * or the next label ends it. Lines before the first label are ignored, so a
 * heading pasted along with the details does no harm, and a field with no
 * value is dropped rather than printing an empty line on the invoice.
 */
export function parseBankDetails(text) {
  const rows = [];
  let current = null;
  for (const raw of (text || "").split("\n")) {
    const line = raw.trim();
    if (!line) {
      current = null;
      continue;
    }
    const m = line.match(LABEL_RE);
    if (m && !m[2].startsWith("//")) {
      current = { label: m[1].trim(), value: m[2].trim() };
      rows.push(current);
      continue;
    }
    if (current) current.value = current.value ? `${current.value} ${line}` : line;
  }
  return rows.filter((r) => r.label && r.value);
}

export default function BankAccountsPanel() {
  const [accounts, setAccounts] = useState([]);
  const [editing, setEditing] = useState(null); // account id, or "new"
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // The paste box: copy the account details off your bank's page, drop them in
  // here, and they become the printed rows in the order they were pasted.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/bank-accounts");
      setAccounts(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load bank accounts");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const closePaste = () => {
    setPasteOpen(false);
    setPasteText("");
  };

  const startNew = () => {
    setForm(BLANK);
    setEditing("new");
    setErr(null);
    closePaste();
  };

  const startEdit = (a) => {
    setForm({
      name: a.name || "",
      currency: a.currency || "",
      note: a.note || "",
      fields: a.fields?.length ? a.fields : [{ label: "", value: "" }],
    });
    setEditing(a.id);
    setErr(null);
    closePaste();
  };

  const setField = (i, key, value) =>
    setForm((f) => ({
      ...f,
      fields: f.fields.map((row, idx) => (idx === i ? { ...row, [key]: value } : row)),
    }));

  const addField = () =>
    setForm((f) => ({ ...f, fields: [...f.fields, { label: "", value: "" }] }));

  const removeField = (i) =>
    setForm((f) => ({ ...f, fields: f.fields.filter((_, idx) => idx !== i) }));

  /** Rows print top to bottom, and a pasted block rarely lands in the order you
   *  want it read (Payoneer puts the beneficiary last), so they can be moved. */
  const moveField = (i, dir) =>
    setForm((f) => {
      const j = i + dir;
      if (j < 0 || j >= f.fields.length) return f;
      const fields = [...f.fields];
      [fields[i], fields[j]] = [fields[j], fields[i]];
      return { ...f, fields };
    });

  const parsedPaste = parseBankDetails(pasteText);

  const applyPaste = () => {
    if (parsedPaste.length === 0) return;
    setForm((f) => {
      // A pasted "Currency: CAD" line fills the currency box too, and the bank's
      // own name becomes the internal label (which is required, so without this
      // a paste leaves you on a form that won't save). Both only when empty, and
      // the rows themselves stay: the invoice prints them.
      const pasted = (re) => parsedPaste.find((r) => re.test(r.label))?.value || "";
      return {
        ...f,
        name: f.name || pasted(/^bank name$/i),
        currency: f.currency || pasted(/^currency$/i),
        fields: parsedPaste,
      };
    });
    closePaste();
  };

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        name: form.name,
        currency: form.currency || null,
        note: form.note || null,
        // Drop half-empty rows: a label with no value prints a blank line on
        // the invoice, which just looks broken.
        fields: form.fields.filter((f) => f.label.trim() && f.value.trim()),
      };
      if (editing === "new") await api.post("/bank-accounts", payload);
      else await api.patch(`/bank-accounts/${editing}`, payload);
      setEditing(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save the account");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    setBusy(true);
    setErr(null);
    try {
      await api.delete(`/bank-accounts/${a.id}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't delete the account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card pad" style={{ maxWidth: 640 }}>
      <h3 style={{ marginTop: 0 }}>Bank accounts</h3>
      <p className="muted" style={{ marginTop: 6 }}>
        Printed as the payment details on an invoice. Pick which account a client
        is billed from on their Users entry.
      </p>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}

      {accounts.length === 0 && editing === null && (
        <p className="muted-sm" style={{ marginTop: 14 }}>No bank accounts yet.</p>
      )}

      {/* A list, not a `.simple` table: that one is built for full-page grids
          and its cell borders read as a spreadsheet inside a settings card. */}
      {accounts.length > 0 && (
        <div style={{ marginTop: 14 }}>
          {accounts.map((a) => (
            <div
              key={a.id}
              className="row-h"
              style={{
                justifyContent: "space-between",
                gap: 12,
                padding: "10px 0",
                borderTop: "1px solid var(--line)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{a.name}</div>
                <div className="muted-sm">
                  {[
                    a.currency,
                    `${a.fields?.length || 0} line${a.fields?.length === 1 ? "" : "s"}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <div className="row-h" style={{ gap: 6, flexShrink: 0 }}>
                <button className="ghost" onClick={() => startEdit(a)} disabled={busy}>
                  Edit
                </button>
                <button
                  className="ghost icon"
                  title="Delete this account"
                  onClick={() => remove(a)}
                  disabled={busy}
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing === null && (
        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 16 }}>
          <button className="primary" onClick={startNew} disabled={busy}>
            Add account
          </button>
        </div>
      )}

      {editing !== null && (
        <div style={{ marginTop: 16, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
          <div className="row-h" style={{ gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="field">Name (internal, never printed)</label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Citibank CAD"
                disabled={busy}
              />
            </div>
            <div style={{ flex: "0 0 120px" }}>
              <label className="field">Currency</label>
              <input
                value={form.currency}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                placeholder="CAD"
                disabled={busy}
              />
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <label className="field">Note above the details (optional)</label>
            <textarea
              rows={2}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="Please pay by local (domestic) CAD bank transfer from a company/business account."
              disabled={busy}
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="row-h" style={{ justifyContent: "space-between", gap: 8 }}>
              <label className="field" style={{ margin: 0 }}>
                Details, printed in this order
              </label>
              <button
                className="ghost"
                onClick={() => (pasteOpen ? closePaste() : setPasteOpen(true))}
                disabled={busy}
              >
                {pasteOpen ? "Cancel paste" : "Paste from your bank"}
              </button>
            </div>

            {pasteOpen && (
              <div style={{ margin: "8px 0 12px" }}>
                <textarea
                  rows={6}
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder={"Bank name:\nCitibank\n\nSWIFT code:\nCITIUS33\n\nAccount number:\n70580850002614545"}
                  style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem" }}
                  autoFocus
                  disabled={busy}
                />
                <div className="row-h" style={{ justifyContent: "space-between", marginTop: 6, gap: 8 }}>
                  <span className="muted-sm">
                    {pasteText.trim()
                      ? `${parsedPaste.length} detail${parsedPaste.length === 1 ? "" : "s"} found`
                      : "Copy the account details off your bank's page and paste them here."}
                  </span>
                  <button
                    className="primary"
                    onClick={applyPaste}
                    disabled={busy || parsedPaste.length === 0}
                  >
                    Use these
                  </button>
                </div>
                {parsedPaste.length > 0 && (
                  <div className="muted-sm" style={{ marginTop: 6 }}>
                    Replaces the lines below: {parsedPaste.map((r) => r.label).join(", ")}
                  </div>
                )}
              </div>
            )}

            {form.fields.map((row, i) => (
              <div className="row-h" style={{ gap: 6, marginBottom: 6, flexWrap: "nowrap" }} key={i}>
                <input
                  style={{ flex: "0 0 38%" }}
                  value={row.label}
                  onChange={(e) => setField(i, "label", e.target.value)}
                  placeholder="Account number"
                  disabled={busy}
                />
                <input
                  style={{ flex: 1, minWidth: 0 }}
                  value={row.value}
                  onChange={(e) => setField(i, "value", e.target.value)}
                  placeholder="3013203817"
                  disabled={busy}
                />
                <button
                  className="ghost icon"
                  title="Move up"
                  onClick={() => moveField(i, -1)}
                  disabled={busy || i === 0}
                >
                  ↑
                </button>
                <button
                  className="ghost icon"
                  title="Move down"
                  onClick={() => moveField(i, 1)}
                  disabled={busy || i === form.fields.length - 1}
                >
                  ↓
                </button>
                <button
                  className="ghost icon"
                  title="Remove this line"
                  onClick={() => removeField(i)}
                  disabled={busy}
                >
                  ×
                </button>
              </div>
            ))}
            <button className="ghost" onClick={addField} disabled={busy}>
              Add line
            </button>
          </div>

          <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 14, gap: 8 }}>
            <button onClick={() => setEditing(null)} disabled={busy}>Cancel</button>
            <button className="primary" onClick={save} disabled={busy || !form.name.trim()}>
              {busy ? "Saving…" : "Save account"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
